"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCopy,
  Download,
  LoaderCircle,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type MediaFormat = "post" | "story" | "carousel" | "pdf";
type ContentCategory = "listing" | "lotw" | "lotd";
type CopyPlatform = "instagram" | "tiktok" | "linkedin";

type CopyData = { text: string };

type Props = {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isStaff?: boolean;
};

const CATEGORIES: Array<{
  value: ContentCategory;
  label: string;
  shortLabel: string;
  staffOnly?: boolean;
}> = [
  { value: "listing", label: "Listing", shortLabel: "Listing" },
  { value: "lotw", label: "Listing of the Week", shortLabel: "LOTW", staffOnly: true },
  { value: "lotd", label: "Listing of the Day", shortLabel: "LOTD", staffOnly: true },
];

const FORMATS: Array<{
  value: MediaFormat;
  label: string;
  listingOnly?: boolean;
}> = [
  { value: "post", label: "Post" },
  { value: "story", label: "Story" },
  { value: "carousel", label: "Carousel" },
  { value: "pdf", label: "PDF Flyer", listingOnly: true },
];

const PLATFORMS: { value: CopyPlatform; label: string }[] = [
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
  { value: "linkedin", label: "LinkedIn" },
];

function buildApiUrl(
  slug: string,
  format: MediaFormat,
  category: ContentCategory,
  opts?: { preview?: boolean; slide?: number },
) {
  const variant = category === "listing" ? "listing" : category;
  let url = `/api/listing-export/${slug}?format=${format}&variant=${variant}`;
  if (opts?.preview) url += "&preview=1";
  if (opts?.slide != null) url += `&slide=${opts.slide}`;
  return url;
}

function triggerDownload(slug: string, format: MediaFormat, category: ContentCategory) {
  const url = buildApiUrl(slug, format, category);
  const link = document.createElement("a");
  link.href = url;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/* ── Inline copy button ── */
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors shrink-0"
    >
      {copied ? <Check className="size-3 text-green-600" /> : <ClipboardCopy className="size-3" />}
      {copied ? "Copied" : label}
    </button>
  );
}

/* ── AI copy sidebar section ── */
function AICopySection({
  slug,
  mediaFormat,
  category,
  copyCache,
}: {
  slug: string;
  mediaFormat: MediaFormat;
  category: ContentCategory;
  copyCache: React.MutableRefObject<Record<string, CopyData>>;
}) {
  const [platform, setPlatform] = useState<CopyPlatform>("instagram");
  const [copyData, setCopyData] = useState<Record<string, CopyData>>(copyCache.current);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const showCopy = mediaFormat !== "pdf";

  const fetchCopy = useCallback(
    async (p: CopyPlatform) => {
      const key = `${category}-${p}`;
      if (copyCache.current[key]) {
        setCopyData({ ...copyCache.current });
        return;
      }
      setLoading(p);
      setError(null);
      try {
        const variant = category === "listing" ? "listing" : category;
        const res = await fetch(`/api/listing-export/${slug}/copy?platform=${p}&variant=${variant}`);
        if (res.ok) {
          const data = await res.json();
          copyCache.current[key] = data;
          setCopyData({ ...copyCache.current });
        } else {
          setError("Failed to generate copy");
        }
      } catch {
        setError("Failed to generate copy");
      } finally {
        setLoading(null);
      }
    },
    [slug, category, copyCache],
  );

  useEffect(() => {
    if (showCopy) fetchCopy(platform);
  }, [platform, showCopy, fetchCopy]);

  if (!showCopy) return null;

  const cacheKey = `${category}-${platform}`;
  const current = copyData[cacheKey];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="size-4 text-green-600" />
        <span className="text-sm font-semibold tracking-tight">Ready-to-post Caption</span>
      </div>

      {/* Platform picker */}
      <div className="inline-flex rounded-full border p-0.5 gap-0.5">
        {PLATFORMS.map((p) => (
          <button
            key={p.value}
            onClick={() => setPlatform(p.value)}
            className={cn(
              "px-3 py-1 text-[11px] font-medium rounded-full transition-all",
              platform === p.value
                ? "bg-foreground text-background shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading === platform && (
        <div className="space-y-2.5 pt-1">
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      )}

      {!loading && current && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Copy to clipboard</span>
            <CopyButton text={current.text} />
          </div>
          <div className="rounded-lg bg-muted/50 p-3 max-h-[200px] overflow-y-auto">
            <p className="text-[13px] leading-relaxed whitespace-pre-wrap">
              {current.text}
            </p>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-2 text-xs text-destructive pt-1">
          <span>{error}</span>
          <button
            onClick={() => fetchCopy(platform)}
            className="underline underline-offset-2 hover:text-destructive/80"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !current && !error && (
        <button
          onClick={() => fetchCopy(platform)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-green-600 hover:text-green-700 transition-colors pt-1"
        >
          <Sparkles className="size-3.5" />
          Generate copy
        </button>
      )}
    </div>
  );
}

/* ── Preview area ── */
function PreviewSection({
  slug,
  mediaFormat,
  category,
  previewCache,
}: {
  slug: string;
  mediaFormat: MediaFormat;
  category: ContentCategory;
  previewCache: React.MutableRefObject<Record<string, string>>;
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const cacheKey = mediaFormat === "carousel"
    ? `${category}-carousel-${currentSlide}`
    : `${category}-${mediaFormat}`;
  const blobUrl = previewCache.current[cacheKey] ?? null;

  const fetchPreview = useCallback((slideIndex?: number, force?: boolean) => {
    const key = mediaFormat === "carousel"
      ? `${category}-carousel-${slideIndex ?? 0}`
      : `${category}-${mediaFormat}`;
    if (!force && previewCache.current[key]) return;

    if (force && previewCache.current[key]) {
      URL.revokeObjectURL(previewCache.current[key]);
      delete previewCache.current[key];
    }

    setLoading(true);
    setError(false);

    let previewUrl = buildApiUrl(slug, mediaFormat, category, {
      preview: true,
      slide: mediaFormat === "carousel" ? (slideIndex ?? 0) : undefined,
    });
    if (force) previewUrl += `&_t=${Date.now()}`;

    let cancelled = false;
    fetch(previewUrl)
      .then((res) => {
        if (!res.ok) throw new Error("Failed");
        const slides = res.headers.get("X-Total-Slides");
        if (slides) setTotalSlides(parseInt(slides, 10));
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        previewCache.current[key] = url;
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug, mediaFormat, category, previewCache]);

  useEffect(() => {
    if (!blobUrl) fetchPreview(currentSlide);
  }, [currentSlide, blobUrl, fetchPreview]);

  useEffect(() => {
    setCurrentSlide(0);
    setTotalSlides(null);
  }, [mediaFormat, category]);

  const isCarousel = mediaFormat === "carousel";
  const isVertical = mediaFormat === "story" || isCarousel;
  const isDev = process.env.NODE_ENV === "development";

  const handleRegenerate = useCallback(() => {
    fetchPreview(currentSlide, true);
  }, [fetchPreview, currentSlide]);

  return (
    <div className="flex flex-col items-center gap-3 w-full h-full">
      {/* Preview canvas */}
      <div className="relative w-full flex-1 min-h-0 rounded-2xl bg-muted/40 flex items-center justify-center overflow-hidden group">
        {loading && (
          <div className="flex flex-col items-center gap-3 py-16">
            <LoaderCircle className="size-5 animate-spin text-muted-foreground/60" />
            <p className="text-xs text-muted-foreground/60">Generating preview&hellip;</p>
          </div>
        )}

        {blobUrl && !loading && (
          <div className="flex items-center justify-center p-4 md:p-6 w-full h-full">
            <div
              className="rounded-xl overflow-hidden shadow-lg ring-1 ring-black/[0.04]"
              style={{ maxWidth: isVertical ? "240px" : mediaFormat === "pdf" ? "300px" : "100%" }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={blobUrl}
                alt="Preview"
                className="w-full h-auto block max-h-[48vh] md:max-h-[56vh] object-contain"
              />
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="flex flex-col items-center gap-2 py-16">
            <p className="text-sm text-muted-foreground">Preview unavailable</p>
            <button
              onClick={() => fetchPreview(currentSlide, true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <RefreshCw className="size-3" />
              Retry
            </button>
          </div>
        )}

        {/* Dev-only regenerate button */}
        {isDev && blobUrl && !loading && (
          <button
            onClick={handleRegenerate}
            className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 text-[11px] font-medium bg-background/90 backdrop-blur-sm border shadow-sm rounded-lg px-2.5 py-1.5 text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="size-3" />
            Regenerate
          </button>
        )}
      </div>

      {/* Carousel pagination */}
      {isCarousel && totalSlides && totalSlides > 1 && (
        <div className="flex items-center gap-2">
          <button
            disabled={currentSlide === 0}
            onClick={() => setCurrentSlide((s) => s - 1)}
            className="size-7 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="size-3.5" />
          </button>

          <div className="flex items-center gap-1">
            {Array.from({ length: totalSlides }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={cn(
                  "size-1.5 rounded-full transition-all",
                  i === currentSlide ? "bg-foreground scale-125" : "bg-muted-foreground/25 hover:bg-muted-foreground/40",
                )}
              />
            ))}
          </div>

          <button
            disabled={currentSlide === totalSlides - 1}
            onClick={() => setCurrentSlide((s) => s + 1)}
            className="size-7 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ChevronRight className="size-3.5" />
          </button>

          <span className="text-[11px] text-muted-foreground/60 tabular-nums ml-0.5">
            {currentSlide + 1}/{totalSlides}
          </span>
        </div>
      )}
    </div>
  );
}

/* ── Main dialog ── */
export function ListingContentDialog({
  slug,
  open,
  onOpenChange,
  isStaff = false,
}: Props) {
  const t = useTranslations();
  const [activeCategory, setActiveCategory] = useState<ContentCategory>("listing");
  const [activeFormats, setActiveFormats] = useState<Record<ContentCategory, MediaFormat>>({
    listing: "post",
    lotw: "post",
    lotd: "post",
  });
  const [downloading, setDownloading] = useState(false);

  const previewCache = useRef<Record<string, string>>({});
  const copyCache = useRef<Record<string, CopyData>>({});

  useEffect(() => {
    if (!open) {
      for (const blobObjUrl of Object.values(previewCache.current)) {
        URL.revokeObjectURL(blobObjUrl);
      }
      previewCache.current = {};
      copyCache.current = {};
    }
  }, [open, slug]);

  const visibleCategories = useMemo(
    () => CATEGORIES.filter((c) => !c.staffOnly || isStaff),
    [isStaff],
  );

  const currentFormat = activeFormats[activeCategory];

  const handleDownload = useCallback(() => {
    setDownloading(true);
    triggerDownload(slug, currentFormat, activeCategory);
    setTimeout(() => setDownloading(false), 3000);
  }, [slug, currentFormat, activeCategory]);

  const downloadLabel =
    currentFormat === "carousel"
      ? "Download All (.zip)"
      : currentFormat === "pdf"
        ? "Download PDF"
        : "Download Image";

  const visibleFormats = FORMATS.filter(
    (f) => !f.listingOnly || activeCategory === "listing",
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        {/* ─── Header ─── */}
        <DialogHeader className="px-6 pt-6 pb-0 space-y-0">
          <DialogTitle className="text-lg font-semibold tracking-tight">
            {t("saas.listing.header.actions.createContent")}
          </DialogTitle>
        </DialogHeader>

        {/* ─── Category navigation (top-level) ─── */}
        {visibleCategories.length > 1 && (
          <div className="px-6 pt-4">
            <div className="inline-flex rounded-full border p-0.5 gap-0.5">
              {visibleCategories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setActiveCategory(cat.value)}
                  className={cn(
                    "px-4 py-1.5 text-xs font-medium rounded-full transition-all",
                    activeCategory === cat.value
                      ? "bg-foreground text-background shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <span className="hidden sm:inline">{cat.label}</span>
                  <span className="sm:hidden">{cat.shortLabel}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ─── Format selector (sub-level) ─── */}
        <div className="px-6 pt-4 pb-3 flex items-center gap-6 border-b">
          {visibleFormats.map((fmt) => (
            <button
              key={fmt.value}
              onClick={() => setActiveFormats((prev) => ({ ...prev, [activeCategory]: fmt.value }))}
              className={cn(
                "pb-3 text-sm font-medium transition-colors relative",
                currentFormat === fmt.value
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground/70",
              )}
            >
              {fmt.label}
              {currentFormat === fmt.value && (
                <span className="absolute bottom-0 inset-x-0 h-0.5 bg-foreground rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* ─── Body: sidebar + preview ─── */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
          {/* Preview (shown first on mobile, right on desktop) */}
          <div className="w-full md:w-[60%] md:order-2 p-5 md:p-6 flex flex-col min-h-[200px] md:min-h-0 overflow-hidden">
            <PreviewSection
              slug={slug}
              mediaFormat={currentFormat}
              category={activeCategory}
              previewCache={previewCache}
            />
          </div>

          {/* Sidebar */}
          <div className="w-full md:w-[40%] md:order-1 md:border-r flex flex-col border-t md:border-t-0 overflow-y-auto">
            <div className="p-5 md:p-6 flex-1 space-y-5">
              <AICopySection
                slug={slug}
                mediaFormat={currentFormat}
                category={activeCategory}
                copyCache={copyCache}
              />

              {currentFormat === "pdf" && (
                <p className="text-[13px] text-muted-foreground leading-relaxed">
                  One-page flyer with photos, financials, operations, and broker contact details.
                </p>
              )}
            </div>

            {/* Download sticky footer */}
            <div className="p-5 md:p-6 pt-0 md:pt-0">
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="w-full gap-2 h-11 rounded-xl"
                size="lg"
              >
                {downloading ? (
                  <>
                    <LoaderCircle className="size-4 animate-spin" />
                    Downloading&hellip;
                  </>
                ) : (
                  <>
                    <Download className="size-4" />
                    {downloadLabel}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
